import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  Dialog,
  Stack,
  TextField,
  Typography,
  DialogActions,
  DialogTitle,
  CircularProgress,
  Paper,
  Alert,
  Tooltip,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  ListItemProps,
  AccordionProps
} from '@mui/material'
import { DatePicker, TimePicker } from '@mui/x-date-pickers'
import { LoadingButton } from '@mui/lab'
import { Delete, Edit, ExpandMore, Upload } from '@mui/icons-material'
import { Asset, AssetInput } from '../types'
import { useTranslation } from 'react-i18next'
import { ReactNode, useEffect, useState } from 'react'
import { useAccordion, useDialog, usePolicy } from '../hooks'
import { deleteAssetPhoto, updateAsset, uploadAssetPhoto } from '../services/assetService'
import {
  createErrorNotification,
  createErrorOrSuccessNotification,
  createSuccessNotification
} from '../redux/slices/notifications'
import { NotificationType } from '../enums/NotificationType'
import { useDispatch } from 'react-redux'
import { Form, Formik, FormikHelpers } from 'formik'
import { humanizeTimestamp } from '../utils/date'
import { isAfter, isBefore } from 'date-fns'
import useAssetTimestamps from '../hooks/useAssetTimestamps'
import AssetDetailsList from './AssetDetailsList'
import Cropper, { Area } from 'react-easy-crop'
import { getCroppedImageBlob, prepareImageForCrop, reduceImageFileSize } from '../utils/imageCrop'
import Dropzone, { DropzoneProps } from './Dropzone'
import ImageWithPreview from './ImageWithPreview'

const ASSET_PHOTO_UPLOAD_MAX_BYTES = 5 * 1024 * 1024
const ASSET_PHOTO_UPLOAD_TARGET_BYTES = 4.8 * 1024 * 1024

interface EditableListItem extends ListItemProps {
  editable?: boolean
}
interface AssetDetailsAttachedListItemProps extends Omit<EditableListItem, 'children'> {
  asset: Asset
  beforeSubmit?: (values: AssetInput, helpers: FormikHelpers<AssetInput>) => void
  afterSubmit?: (asset: Asset, helpers: FormikHelpers<AssetInput>) => void
}

function AssetDetailsAttachedListItem({
  asset,
  editable = true,
  beforeSubmit,
  afterSubmit,
  ...props
}: AssetDetailsAttachedListItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy context.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The attachment timestamps.
   */
  const { attachedAt } = useAssetTimestamps(asset)

  /**
   * The intial values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId,
    attachedAt
  }

  async function handleSubmit(
    values: AssetInput,
    { setSubmitting, ...helpers }: FormikHelpers<AssetInput>
  ) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('asset:assetUpdateSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset, { setSubmitting, ...helpers })
      closeDialog()
    } catch (_error) {
      dispatch(createErrorNotification(t('asset:assetUpdateFailed')))
      setSubmitting(false)
    }
  }

  return (
    <>
      {isSuperadmin && (
        <>
          <ListItem key="attached" disableGutters disablePadding {...props}>
            <ListItemButton>
              <ListItemText
                primary={
                  asset?.attachedAt
                    ? humanizeTimestamp(asset?.attachedAt)
                    : humanizeTimestamp(attachedAt)
                }
                secondary={t('asset:attachedAt')}
              />
              {editable && (
                <ListItemSecondaryAction>
                  <Tooltip title={t('edit') as string}>
                    <IconButton onClick={openDialog}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItemButton>
          </ListItem>

          <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
            <DialogTitle>{t('asset:selectAttachedAt')}</DialogTitle>
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              {({ values, isSubmitting, setFieldValue, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <DialogContent>
                    <Stack direction="row" alignItems="center" gap={3}>
                      <DatePicker
                        label={t('date')}
                        value={values?.attachedAt ?? null}
                        onChange={(value) => {
                          if (
                            value === null ||
                            asset?.detachedAt === undefined ||
                            asset?.detachedAt === null ||
                            isBefore(value, new Date(asset.detachedAt))
                          ) {
                            setFieldValue('attachedAt', value)
                          }
                        }}
                      />
                      <TimePicker
                        label={t('time')}
                        value={values?.attachedAt ?? null}
                        sx={{ width: '100px' }}
                        onChange={(value) => {
                          if (
                            value === null ||
                            asset?.detachedAt === undefined ||
                            asset?.detachedAt === null ||
                            isBefore(value, new Date(asset.detachedAt))
                          ) {
                            setFieldValue('attachedAt', value)
                          }
                        }}
                        // @ts-expect-error typed incorrectly
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="text" color="inherit" onClick={closeDialog}>
                      {t('cancel')}
                    </Button>

                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={isSubmitting}
                    >
                      {t('save')}
                    </LoadingButton>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Dialog>
        </>
      )}
    </>
  )
}

interface AssetDetailsDetachedListItemProps extends Omit<EditableListItem, 'children'> {
  asset: Asset
  beforeSubmit?: (values: AssetInput, helpers: FormikHelpers<AssetInput>) => void
  afterSubmit?: (asset: Asset, helpers: FormikHelpers<AssetInput>) => void
}

function AssetDetailsDetachedListItem({
  asset,
  editable = true,
  beforeSubmit,
  afterSubmit,
  ...props
}: AssetDetailsDetachedListItemProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy context.
   */
  const { isSuperadmin } = usePolicy()

  /**
   * The dialog state.
   */
  const { open, openDialog, closeDialog } = useDialog()

  /**
   * The detachedAt timestamp.
   */
  const { detachedAt } = useAssetTimestamps(asset)

  /**
   * The intial values.
   */
  const initialValues: AssetInput = {
    name: asset.name,
    company: asset.companyId,
    project: asset.projectId,
    level: asset.levelId,
    lift: asset.liftId,
    detachedAt
  }

  async function handleSubmit(
    values: AssetInput,
    { setSubmitting, ...helpers }: FormikHelpers<AssetInput>
  ) {
    try {
      setSubmitting(true)

      beforeSubmit?.(values, { setSubmitting, ...helpers })

      const updatedAsset = await updateAsset(asset.id, values)
      dispatch(createSuccessNotification(t('asset:assetUpdateSuccess')))

      setSubmitting(false)
      afterSubmit?.(updatedAsset, { setSubmitting, ...helpers })
      closeDialog()
    } catch (_error) {
      dispatch(createErrorNotification(t('asset:assetUpdateFailed')))
      setSubmitting(false)
    }
  }

  return (
    <>
      {isSuperadmin && detachedAt !== null && (
        <>
          <ListItem disableGutters disablePadding {...props}>
            <ListItemButton>
              <ListItemText
                primary={
                  asset?.detachedAt
                    ? humanizeTimestamp(asset.detachedAt)
                    : detachedAt
                      ? humanizeTimestamp(detachedAt)
                      : '-'
                }
                secondary={t('asset:detachedAt')}
              />

              {editable && (
                <ListItemSecondaryAction>
                  <Tooltip title={t('edit') as string}>
                    <IconButton onClick={openDialog}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              )}
            </ListItemButton>
          </ListItem>

          <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
            <DialogTitle>{t('asset:selectDetachedAt')}</DialogTitle>
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              {({ values, isSubmitting, setFieldValue, handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <DialogContent>
                    <Stack direction="row" alignItems="center" gap={3}>
                      <DatePicker
                        label={t('date')}
                        value={values?.detachedAt ?? null}
                        onChange={(value: Date | null) => {
                          if (
                            value === null ||
                            (asset?.attachedAt !== undefined &&
                              asset.attachedAt !== null &&
                              isAfter(value, new Date(asset.attachedAt)))
                          ) {
                            setFieldValue('detachedAt', value)
                          }
                        }}
                      />

                      <TimePicker
                        label={t('time')}
                        value={values?.detachedAt ?? null}
                        sx={{ width: '100px' }}
                        onChange={(value) => {
                          if (
                            value === null ||
                            (asset?.attachedAt !== undefined &&
                              asset.attachedAt !== null &&
                              isAfter(value, new Date(asset.attachedAt)))
                          ) {
                            setFieldValue('detachedAt', value)
                          }
                        }}
                        // @ts-expect-error typed incorrectly
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="text" color="inherit" onClick={closeDialog}>
                      {t('cancel')}
                    </Button>

                    <LoadingButton
                      type="submit"
                      variant="contained"
                      color="primary"
                      loading={isSubmitting}
                    >
                      {t('save')}
                    </LoadingButton>
                  </DialogActions>
                </Form>
              )}
            </Formik>
          </Dialog>
        </>
      )}
    </>
  )
}

interface AssetDetailsProps extends Omit<AccordionProps, 'expanded' | 'onChange' | 'children'> {
  children?: ReactNode
  asset: Asset | null
  editable?: boolean
  onUpdate?: (asset: Asset) => void
}

export default function AssetDetails({
  asset,
  editable = true,
  onUpdate,
  children,
  defaultExpanded,
  ...props
}: AssetDetailsProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation()

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The policy context.
   */
  const { canEditAsset } = usePolicy()

  /**
   * The uploaded file.
   */
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const [cropMode, setCropMode] = useState<'normal' | 'square'>('normal')
  const [imageAspect, setImageAspect] = useState<number>(1)

  /**
   * Indicates if the component photo is being uploaded.
   */
  const [isUploadingPhoto, setIsUploadingPhoto] = useState<boolean>(false)

  /**
   * Indicates if the photo is being deleted.
   */
  const [isDeletingPhoto, setIsDeletingPhoto] = useState<boolean>(false)

  /**
   * Indicates if the photo dialog is open.
   */
  const {
    open: isPhotoDialogOpen,
    openDialog: openPhotoDialogInternal,
    closeDialog: closePhotoDialogInternal
  } = useDialog()

  const {
    open: isPhotoSelectDialogOpen,
    openDialog: openPhotoSelectDialogInternal,
    closeDialog: closePhotoSelectDialogInternal
  } = useDialog()

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  useEffect(() => {
    if (!previewUrl) {
      setImageAspect(1)
      return
    }

    let cancelled = false
    const image = new Image()

    image.onload = () => {
      if (!cancelled && image.naturalWidth > 0 && image.naturalHeight > 0) {
        setImageAspect(image.naturalWidth / image.naturalHeight)
      }
    }

    image.src = previewUrl

    return () => {
      cancelled = true
    }
  }, [previewUrl])

  /**
   * The accordion state.
   */
  const { expanded, toggleAccordion } = useAccordion(defaultExpanded)

  function openPhotoDialog(): void {
    openPhotoDialogInternal()
  }

  function openPhotoSelectDialog(): void {
    openPhotoSelectDialogInternal()
  }

  function closePhotoDialog(): void {
    closePhotoDialogInternal()
    setPendingFile(null)
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setPreviewUrl(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setCropMode('normal')
    setImageAspect(1)
  }

  function closePhotoSelectDialog(): void {
    closePhotoSelectDialogInternal()
  }

  function dispatchError(message: string) {
    dispatch(createErrorOrSuccessNotification(NotificationType.WARNING, message))
  }

  async function handlePhotoFile(newFile: File): Promise<void> {
    if (!newFile.type.startsWith('image/')) {
      return
    }

    closePhotoSelectDialog()

    let fileForCrop = newFile
    try {
      fileForCrop = await prepareImageForCrop(newFile)
    } catch (_error) {
      fileForCrop = newFile
    }

    setPendingFile(fileForCrop)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setCropMode('normal')

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    setPreviewUrl(URL.createObjectURL(fileForCrop))
    openPhotoDialog()
  }

  const handlePhotoDrop: DropzoneProps['onDrop'] = async (acceptedFiles): Promise<void> => {
    const newFile = acceptedFiles?.[0]
    if (newFile) {
      await handlePhotoFile(newFile)
    }
  }

  function handleCropModeChange(_event: unknown, mode: 'normal' | 'square' | null): void {
    if (mode === null) {
      return
    }

    setCropMode(mode)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }

  const handleCropComplete = (_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }

  /**
   * Upload photo.
   */
  async function uploadPhoto(): Promise<void> {
    try {
      setIsUploadingPhoto(true)

      const currentFile = pendingFile
      if (asset !== null && currentFile !== null) {
        let uploadFile = currentFile
        if (previewUrl && croppedAreaPixels) {
          const blob = await getCroppedImageBlob(previewUrl, croppedAreaPixels, currentFile.type)
          uploadFile = new File([blob], currentFile.name, {
            type: currentFile.type,
            lastModified: Date.now()
          })
        }

        uploadFile = await reduceImageFileSize(uploadFile, ASSET_PHOTO_UPLOAD_TARGET_BYTES)

        if (uploadFile.size > ASSET_PHOTO_UPLOAD_MAX_BYTES) {
          throw new Error('FILE_TOO_LARGE')
        }

        onUpdate?.(await uploadAssetPhoto(asset.id, uploadFile))
      }

      setIsUploadingPhoto(false)
      closePhotoDialog()
    } catch (error) {
      if (error instanceof Error && error.message === 'FILE_TOO_LARGE') {
        dispatchError('Photo is too large. Please choose a smaller image.')
      } else {
        dispatchError('Failed to upload photo!')
      }
      setIsUploadingPhoto(false)
    }
  }

  /**
   * Delete the asset photo.
   */
  async function deletePhoto(): Promise<void> {
    try {
      setIsDeletingPhoto(true)

      if (asset !== null) {
        onUpdate?.(await deleteAssetPhoto(asset.id))
      }

      setIsDeletingPhoto(false)
    } catch (_error) {
      dispatchError('Failed to delete asset photo!')
      setIsDeletingPhoto(false)
    }
  }

  return (
    <Accordion expanded={expanded} onChange={toggleAccordion} {...props}>
      <AccordionSummary expandIcon={<ExpandMore />} sx={{ pr: 6 }}>
        <Box px={3}>
          <Typography variant="h6">{t('assetInformation')}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {asset ? (
          <>
            <AssetDetailsList
              asset={asset}
              editable={editable}
              disablePadding
              afterUpdate={onUpdate}
            />
            <List>
              {asset?.photoUrl ? (
                <ListItem key="photo" disableGutters disablePadding>
                  <ListItemButton>
                    {asset?.photoUrl ? (
                      <Paper elevation={6}>
                        <ImageWithPreview
                          src={asset.photoUrl}
                          alt={asset.name}
                          style={{
                            display: 'block',
                            objectFit: 'contain',
                            width: 'auto',
                            height: '64px',
                            maxWidth: '96px',
                            borderRadius: '4px'
                          }}
                          previewMaxWidth="lg"
                        />
                      </Paper>
                    ) : (
                      <CircularProgress />
                    )}

                    {canEditAsset(asset) && (
                      <ListItemSecondaryAction>
                        <Stack direction="row" gap={1}>
                          <Tooltip title={t('deletePhoto') as string}>
                            <LoadingButton
                              component={IconButton}
                              color="error"
                              size="small"
                              disabled={isDeletingPhoto}
                              loading={isDeletingPhoto}
                              onClick={deletePhoto}
                              sx={{ borderRadius: '50%', p: 2, minWidth: 0 }}
                            >
                              <Delete />
                            </LoadingButton>
                          </Tooltip>

                          <Tooltip title={t('uploadNewPhoto') as string}>
                            <Button
                              variant="text"
                              size="small"
                              sx={{ borderRadius: '50%', p: 2, minWidth: 0 }}
                              onClick={openPhotoSelectDialog}
                            >
                              <Upload />
                            </Button>
                          </Tooltip>
                        </Stack>
                      </ListItemSecondaryAction>
                    )}
                  </ListItemButton>
                </ListItem>
              ) : (
                <>
                  {canEditAsset(asset) && (
                    <ListItem key="photo" sx={{ py: 4 }}>
                      <Button variant="outlined" size="small" onClick={openPhotoSelectDialog}>
                        {t('addPhoto')}
                      </Button>
                    </ListItem>
                  )}
                </>
              )}

              <Dialog
                open={isPhotoSelectDialogOpen}
                onClose={closePhotoSelectDialog}
                fullWidth
                maxWidth="xs"
              >
                <DialogTitle>{t('uploadNewPhoto')}</DialogTitle>
                <DialogContent>
                  <Dropzone
                    icon={<Upload color="disabled" sx={{ fontSize: 48 }} />}
                    label={t('uploadNewPhoto')}
                    minHeight={150}
                    height="auto"
                    onDrop={handlePhotoDrop}
                  />
                </DialogContent>
                <DialogActions>
                  <Button color="inherit" onClick={closePhotoSelectDialog}>
                    {t('cancel')}
                  </Button>
                </DialogActions>
              </Dialog>

              <Dialog open={isPhotoDialogOpen} onClose={closePhotoDialog} fullWidth maxWidth="sm">
                <DialogTitle>{t('previewPhoto')}</DialogTitle>
                <DialogContent>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: 320,
                      bgcolor: 'grey.900',
                      borderRadius: 2,
                      overflow: 'hidden'
                    }}
                  >
                    {previewUrl && (
                      <Cropper
                        image={previewUrl}
                        crop={crop}
                        zoom={zoom}
                        aspect={cropMode === 'square' ? 1 : imageAspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={handleCropComplete}
                      />
                    )}
                  </Box>
                  <Box sx={{ mt: 3, px: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('crop')}
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={cropMode}
                      onChange={handleCropModeChange}
                    >
                      <ToggleButton value="normal">{t('normal')}</ToggleButton>
                      <ToggleButton value="square">{t('square')}</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  <Box sx={{ mt: 3, px: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('zoom')}
                    </Typography>
                    <Slider
                      value={zoom}
                      min={1}
                      max={3}
                      step={0.1}
                      onChange={(_, value) => setZoom(value as number)}
                    />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button disabled={isUploadingPhoto} onClick={closePhotoDialog}>
                    {t('cancel')}
                  </Button>

                  <LoadingButton
                    variant="contained"
                    disabled={isUploadingPhoto}
                    loading={isUploadingPhoto}
                    onClick={uploadPhoto}
                  >
                    {t('save')}
                  </LoadingButton>
                </DialogActions>
              </Dialog>
              <AssetDetailsAttachedListItem
                key="attached"
                asset={asset}
                editable={editable}
                afterSubmit={onUpdate}
              />

              <AssetDetailsDetachedListItem
                key="detached"
                asset={asset}
                editable={editable}
                afterSubmit={onUpdate}
              />
            </List>
          </>
        ) : (
          <Alert variant="outlined" severity="error" sx={{ border: 0 }}>
            {t('assetNotFound')}
          </Alert>
        )}

        {children}
      </AccordionDetails>
    </Accordion>
  )
}
